import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { TelemetryService } from 'src/app/service/telemetry.service';
import { TelemetryData, TelemetryConnectionStatus } from 'src/app/model/telemetry-data';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-telemetry-realtime',
  templateUrl: './telemetry-realtime.component.html',
  styleUrls: ['./telemetry-realtime.component.css']
})
export class TelemetryRealtimeComponent implements OnInit, OnDestroy {
  @Input() carId!: string;
  @Input() autoConnect: boolean = true;
  @Output() dataReceived = new EventEmitter<TelemetryData>();
  @Output() connectionStatusChanged = new EventEmitter<TelemetryConnectionStatus>();
  
  currentData: TelemetryData | null = null;
  connectionStatus: TelemetryConnectionStatus = { status: 'disconnected' };
  lastUpdateTime: Date | null = null;
  dataHistory: TelemetryData[] = [];
  maxHistorySize: number = 100;
  
  private subscription = new Subscription();

  constructor(private telemetryService: TelemetryService) {}

  ngOnInit(): void {
    if (!this.carId) {
      console.error('[TelemetryRealtime] carId is required');
      return;
    }

    if (this.autoConnect) {
      this.connect();
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Conecta ao stream de telemetria
   */
  connect(): void {
    //console.log(`[TelemetryRealtime] Connecting to car: ${this.carId}`);
    
    // Primeiro, tenta buscar a última posição conhecida do Redis
    this.telemetryService.getLastKnownPosition(this.carId).subscribe({
      next: (data) => {
        //console.log('[TelemetryRealtime] Last known position loaded:', data);
        this.updateData(data);
      },
      error: (error) => {
        //console.log('[TelemetryRealtime] No last known position available');
      }
    });

    // Conecta ao stream em tempo real via SSE
    const streamSub = this.telemetryService.connectToRealtime(this.carId).subscribe({
      next: (data) => {
        this.updateData(data);
        this.dataReceived.emit(data);
      },
      error: (error) => {
        //console.error('[TelemetryRealtime] Stream error:', error);
      }
    });

    // Monitora status da conexão
    const statusSub = this.telemetryService.getConnectionStatus().subscribe(
      status => {
        this.connectionStatus = status;
        this.connectionStatusChanged.emit(status);
      }
    );

    this.subscription.add(streamSub);
    this.subscription.add(statusSub);
  }

  /**
   * Desconecta do stream
   */
  disconnect(): void {
    //console.log('[TelemetryRealtime] Disconnecting');
    this.subscription.unsubscribe();
    this.telemetryService.disconnect();
    this.subscription = new Subscription();
  }

  /**
   * Reconecta ao stream
   */
  reconnect(): void {
    this.disconnect();
    setTimeout(() => this.connect(), 500);
  }

  /**
   * Atualiza dados recebidos
   */
  private updateData(data: TelemetryData): void {
    this.currentData = data;
    this.lastUpdateTime = new Date();
    
    // Adiciona ao histórico local
    this.dataHistory.push(data);
    
    // Mantém apenas os últimos N pontos
    if (this.dataHistory.length > this.maxHistorySize) {
      this.dataHistory.shift();
    }
    
    //console.log('[TelemetryRealtime] Data updated:', data);
  }

  /**
   * Formata velocidade para exibição
   */
  getSpeedDisplay(): string {
    if (!this.currentData?.speed) return '--';
    return `${this.currentData.speed.toFixed(0)} km/h`;
  }

  /**
   * Formata coordenadas
   */
  getCoordinatesDisplay(): string {
    if (!this.currentData) return '--';
    return `${this.currentData.latitude.toFixed(6)}°, ${this.currentData.longitude.toFixed(6)}°`;
  }

  /**
   * Verifica se dados estão atualizados (últimos 30 segundos)
   */
  isDataFresh(): boolean {
    if (!this.lastUpdateTime) return false;
    const diff = Date.now() - this.lastUpdateTime.getTime();
    return diff < 30000; // 30 segundos
  }

  /**
   * Tempo desde última atualização
   */
  getTimeSinceUpdate(): string {
    if (!this.lastUpdateTime) return 'Nunca';
    
    const diff = Date.now() - this.lastUpdateTime.getTime();
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return `${seconds}s atrás`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m atrás`;
    return `${Math.floor(seconds / 3600)}h atrás`;
  }
}
